// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Input,
    InputOnChangeData,
    makeStyles,
    mergeClasses,
    shorthands,
    Subtitle2Stronger,
    Text,
    tokens,
} from '@fluentui/react-components';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useChat, useFile } from '../../../libs/hooks';
import { getFriendlyChatName } from '../../../libs/hooks/useChat';
import { AlertType } from '../../../libs/models/AlertType';
import { ChatArchive } from '../../../libs/models/ChatArchive';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { addAlert } from '../../../redux/features/app/appSlice';
import { FeatureKeys } from '../../../redux/features/app/AppState';
import { Conversations } from '../../../redux/features/conversations/ConversationsState';
import { Breakpoints, customTokens } from '../../../styles';
import { FileUploader } from '../../FileUploader';
import { Dismiss20, Filter20 } from '../../shared/BundledIcons';
import { isToday } from '../../utils/TextUtils';
import { NewBotMenu } from './bot-menu/NewBotMenu';
import { SimplifiedNewBotMenu } from './bot-menu/SimplifiedNewBotMenu';
import { ChatListSection } from './ChatListSection';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexShrink: 0,
        width: '320px',
        backgroundColor: tokens.colorNeutralBackground3,
        flexDirection: 'column',
        ...shorthands.overflow('hidden'),
        ...Breakpoints.small({
            width: '64px',
        }),
    },
    list: {
        ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXS, 0, 0),
        ...shorthands.margin(tokens.spacingVerticalXS, tokens.spacingHorizontalXS),
        overflowY: 'auto',
        overflowX: 'hidden',
        '&:hover': {
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: tokens.colorScrollbarOverlay,
                visibility: 'visible',
            },
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: tokens.colorSubtleBackground,
        },
        alignItems: 'stretch',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingRight: tokens.spacingVerticalM,
        paddingLeft: tokens.spacingHorizontalXL,
        alignItems: 'center',
        height: '60px',
        minHeight: '60px',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: tokens.colorNeutralBackground6,
        ...Breakpoints.small({
            justifyContent: 'center',
        }),
    },
    title: {
        flexGrow: 1,
        ...Breakpoints.small({
            display: 'none',
        }),
        fontSize: tokens.fontSizeBase500,
    },
    input: {
        flexGrow: 1,
        ...shorthands.padding(tokens.spacingHorizontalNone),
        ...shorthands.border(tokens.borderRadiusNone),
        backgroundColor: tokens.colorSubtleBackground,
        fontSize: tokens.fontSizeBase500,
    },
    spacer: {
        flexGrow: 1,
    },
    logo: {
        textAlign: 'center',
        marginTop: '12px',
        ...Breakpoints.small({
            display: 'none',
        }),
        '& > svg': {
            color: customTokens.colorBackgroundOverlay,
            width: '50%',
        },
    },
    version: {
        ...shorthands.padding('3px', '10px'),
        textAlign: 'center',
        color: customTokens.colorBackgroundOverlay,
        ...Breakpoints.small({
            display: 'none',
        }),
    },
});

interface ConversationsView {
    latestConversations?: Conversations;
    olderConversations?: Conversations;
}

export const ChatList: FC = () => {
    const classes = useClasses();
    const { serviceInfo, features } = useAppSelector((state: RootState) => state.app);
    const { conversations } = useAppSelector((state: RootState) => state.conversations);

    const [isFiltering, setIsFiltering] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [conversationsView, setConversationsView] = useState<ConversationsView>({
        latestConversations: conversations,
    });

    const chat = useChat();
    const fileHandler = useFile();
    const dispatch = useAppDispatch();

    const sortConversations = (conversations: Conversations): ConversationsView => {
        // sort conversations by last activity
        const sortedIds = Object.keys(conversations).sort((a, b) => {
            if (conversations[a].lastUpdatedTimestamp === undefined) {
                return 1;
            }
            if (conversations[b].lastUpdatedTimestamp === undefined) {
                return -1;
            }
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion,@typescript-eslint/no-unnecessary-type-assertion
            return conversations[b].lastUpdatedTimestamp! - conversations[a].lastUpdatedTimestamp!;
        });

        // Add conversations to sortedConversations in the order of sortedIds.
        const latestConversations: Conversations = {};
        const olderConversations: Conversations = {};
        sortedIds.forEach((id) => {
            if (isToday(new Date(conversations[id].lastUpdatedTimestamp ?? 0))) {
                latestConversations[id] = conversations[id];
            } else {
                olderConversations[id] = conversations[id];
            }
        });
        return {
            latestConversations: latestConversations,
            olderConversations: olderConversations,
        };
    };

    useEffect(() => {
        // Ensure local component state is in line with app state.
        const nonHiddenConversations: Conversations = {};
        for (const key in conversations) {
            const conversation = conversations[key];
            if (
                !conversation.hidden &&
                (!filterText ||
                    getFriendlyChatName(conversation).toLocaleUpperCase().includes(filterText.toLocaleUpperCase()))
            ) {
                nonHiddenConversations[key] = conversation;
            }
        }

        setConversationsView(sortConversations(nonHiddenConversations));
    }, [conversations, filterText]);

    const onFilterClick = () => {
        setIsFiltering(true);
    };

    const onFilterCancel = () => {
        setFilterText('');
        setIsFiltering(false);
    };

    const onSearch = (ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        ev.preventDefault();
        setFilterText(data.value);
    };

    const fileUploaderRef = useRef<HTMLInputElement>(null);
    const onUpload = useCallback(
        (file: File) => {
            fileHandler.loadFile<ChatArchive>(file, chat.uploadBot).catch((error) =>
                dispatch(
                    addAlert({
                        message: `Failed to parse uploaded file. ${error instanceof Error ? error.message : ''}`,
                        type: AlertType.Error,
                    }),
                ),
            );
        },
        [fileHandler, chat, dispatch],
    );

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                {features[FeatureKeys.SimplifiedExperience].enabled ? (
                    <SimplifiedNewBotMenu onFileUpload={() => fileUploaderRef.current?.click()} />
                ) : (
                    <>
                        {!isFiltering && (
                            <>
                                <Subtitle2Stronger className={classes.title}>Conversations</Subtitle2Stronger>
                                <Button icon={<Filter20 />} appearance="transparent" onClick={onFilterClick} />
                                <NewBotMenu onFileUpload={() => fileUploaderRef.current?.click()} />
                                <FileUploader
                                    ref={fileUploaderRef}
                                    acceptedExtensions={['.json']}
                                    onSelectedFile={onUpload}
                                />
                            </>
                        )}
                        {isFiltering && (
                            <>
                                <Input
                                    placeholder="Filter by name"
                                    className={mergeClasses(classes.input, classes.title)}
                                    onChange={onSearch}
                                    autoFocus
                                />
                                <Button icon={<Dismiss20 />} appearance="transparent" onClick={onFilterCancel} />
                            </>
                        )}
                    </>
                )}
            </div>
            <div aria-label={'chat list'} className={classes.list}>
                {conversationsView.latestConversations && (
                    <ChatListSection header="Heutige Chats" conversations={conversationsView.latestConversations} />
                )}
                {conversationsView.olderConversations && (
                    <ChatListSection header="Ältere Chats" conversations={conversationsView.olderConversations} />
                )}
            </div>
            <div className={classes.spacer} />
            <div className={classes.logo}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1500 245.82">
                    <path
                        d="M660.28 92.89a39.41 39.41 0 0117.8-4.21c17.5-.33 31.72 10 37.85 26.17 1.34 3 3.23 9.08 3.23 18 0 25-15.43 36.69-23.9 40.6-3.33 1.66-8.84 3.57-17.48 3.57-25 0-40.14-20.22-40.45-43.54 0-24.37 14.47-36.36 22.95-40.59M628.27 0l-13.63 35.36-3.21 1.93a109.82 109.82 0 00-46.66 67.13 127.94 127.94 0 00-3.36 28.74c0 48.89 26.84 88.21 72.93 104.7a138.34 138.34 0 0046.54 8c66.32 0 114.2-47.29 114.2-113.59a131.92 131.92 0 00-3.36-30.27c-9.14-35.06-34.45-55.51-44.52-62.21l-7.4-4.68L753.3 0h-52.37l-7.79 20.24a130.75 130.75 0 00-15.95-.66h-4.06L680.63 0zm413.9 121.79V81.48h12.25c12.87 0 26.51 2.53 26.51 19.69a21.67 21.67 0 01-3.39 12.11c-5.53 8.3-14.88 8.51-22.81 8.51zm17.21-96.08h-88.79V241.1h71.58v-85.73l43.37 85.73h80.83l-52.5-86.9c7.32-2.66 12.72-4.74 19.22-9.18 9.56-6.87 22.21-20.33 22.21-48.19 0-22.12-8.48-35.1-13.91-42-15.37-20.2-37.74-26.94-62.62-28.48-6.45-.61-12.94-.61-19.39-.61m-620.75 96.05V81.48h12.25c12.87 0 26.5 2.53 26.5 19.69a21.67 21.67 0 01-3.38 12.11c-5.53 8.3-14.88 8.51-22.8 8.51zm17.2-96.08H367V241.1h71.59v-85.73L482 241.1h80.83l-52.5-86.9c7.32-2.66 12.73-4.74 19.22-9.18 9.56-6.87 22.21-20.33 22.21-48.19 0-22.12-8.47-35.1-13.9-42-15.37-20.2-37.74-26.94-62.63-28.48-6.45-.61-12.93-.61-19.39-.61m-269.87-.03v62.28h43.39l-.01 153.11h77.17V87.99h43.7V25.71H185.97zM808.99 241.1h145.64v-57.64h-74.68v-23.87h66.94v-54.85h-66.94v-21.4h72.52V25.71H808.99V241.1zM153.35 29.72c-17-6.66-36.79-11.08-55.12-11.08-32.8 0-65.81 12.57-80.91 43.37A75.26 75.26 0 009.44 96c0 37.91 27.83 50.82 40.68 55.41a268.94 268.94 0 0032 7.38l5 1.07c6.16 1.51 12.15 4.49 12.15 12.08 0 8.25-8.48 15-22.48 15-19.39 0-38.05-12.4-52.86-22.65L0 221c28.59 15 54.69 23.86 87.69 23.86 25.72 0 46.35-5.78 65.49-22.8 22.15-20.32 24.82-42.7 24.82-54.5 0-14.57-3.88-43.1-39.5-57-9.24-3.39-25.22-7.07-34.77-9.22l-5.21-1.06C92.48 98.89 86 96.08 86 88.52c0-8.39 7.47-12.55 19.66-12.86 17.2 0 31.41 8 45.38 16.27l21.86-53.38c-4.82-2.31-11.29-5.59-19.55-8.83m1083.6-4.05h99.82l82.29 107.92-82.29 107.92h-99.82l80.26-107.92-80.26-107.92zm193.55 122.4l-50.79 66.61 20.46 26.83H1500l-69.5-93.44zm0-28.96l69.5-93.44h-99.83l-20.46 26.84 50.79 66.6z"
                        fill="currentColor"
                    ></path>
                </svg>
            </div>
            <div className={classes.version}>
                <Text size={100}>build {serviceInfo.version}</Text>
            </div>
        </div>
    );
};
